/**
 * How each kind of chat file is drawn and named — one map shared by the
 * composer's chips, the bubble's tiles and the viewer's file card.
 */
import type { ThreadFileKind } from '@discovr/types';

import { FileDoc, FileText, FilmStrip, Image, Presentation, Table, type Icon } from '../../../design-system/icons';
import { formatBytes } from '../../../lib/format';
import { ENCODED_IMAGE_EXTENSION, isCompressedContentType } from '../../../lib/media/compressImage';

/** Phosphor (the design system's icon set) glyphs for each kind of file; see `FileKindIcon`. */
export const KIND_ICON: Record<ThreadFileKind, Icon> = {
  image: Image,
  video: FilmStrip,
  pdf: FileText,
  document: FileDoc,
  spreadsheet: Table,
  presentation: Presentation,
};

/** The kind as a file's meta line names it: `PDF · 2.4 MB`. */
const KIND_LABEL: Record<ThreadFileKind, string> = {
  image: 'Image',
  video: 'Video',
  pdf: 'PDF',
  document: 'Word',
  spreadsheet: 'Excel',
  presentation: 'PowerPoint',
};

/**
 * A kind this build doesn't know — a newer server's, or a message missing
 * it — is named as a plain file (and drawn as one, see `FileKindIcon`).
 */
const UNKNOWN_KIND_LABEL = 'File';

/** The kind is one this build draws. The server's value is checked, not trusted. */
export function isKnownKind(kind: string): kind is ThreadFileKind {
  return Object.hasOwn(KIND_ICON, kind);
}

export function kindLabel(kind: string): string {
  if (!isKnownKind(kind)) return UNKNOWN_KIND_LABEL;
  return KIND_LABEL[kind];
}

/** A moment's frame carries only its id and dimensions, so its size is unknown. */
export const UNKNOWN_FILE_SIZE = 0;

/**
 * Image types an `<img>` can't draw outside Safari. A thumbnail of one would
 * download the whole original only to fail decoding, so it shows as a tile;
 * the viewer still opens it.
 */
const NO_INLINE_PREVIEW_TYPES: ReadonlySet<string> = new Set(['image/heic', 'image/heif']);

export function hasInlinePreview(contentType: string): boolean {
  return !NO_INLINE_PREVIEW_TYPES.has(contentType);
}

const META_SEPARATOR = ' · ';

export function hasKnownSize(size: number): boolean {
  return size > UNKNOWN_FILE_SIZE;
}

/** `PDF · 2.4 MB` — or just the kind when the size is unknown. */
export function fileMetaLabel(kind: ThreadFileKind, size: number): string {
  if (!hasKnownSize(size)) return kindLabel(kind);
  return `${kindLabel(kind)}${META_SEPARATOR}${formatBytes(size)}`;
}

const NO_EXTENSION = '';

/**
 * The extension for a moment frame's stored type — always a format the
 * browser's encoder wrote (`.webp`, `.jpg`); none for anything else.
 */
export function extensionForContentType(contentType: string | undefined): string {
  if (!isCompressedContentType(contentType)) return NO_EXTENSION;
  return ENCODED_IMAGE_EXTENSION[contentType];
}
